import * as THREE from 'three';
import { CollabAvatarData, COLLAB_CONFIG } from './fiko-collab-types';

/**
 * FikoCollabAvatar - 3D avatar showing another user's camera position
 *
 * Displays:
 * - A sprite with user's initial/avatar at their camera position
 * - A view frustum cone showing their view direction
 * - A label with the user's name
 */
export class FikoCollabAvatar extends THREE.Group {
    readonly type = 'FikoCollabAvatar';

    private userId: string;
    private userName: string;
    private userColor: string;

    // 3D elements
    private avatarSprite: THREE.Sprite;
    private viewCone: THREE.Mesh;
    private nameLabel: THREE.Sprite;

    // State
    private isActive: boolean = true;

    // Interpolation
    private targetPosition = new THREE.Vector3();
    private targetLookAt = new THREE.Vector3();

    constructor(userId: string, userName: string, userColor: string) {
        super();

        this.userId = userId;
        this.userName = userName;
        this.userColor = userColor || '#00FFFF';

        this.name = `CollabAvatar_${userId}`;

        // Create avatar sprite
        this.avatarSprite = this.createAvatarSprite();
        this.add(this.avatarSprite);

        // Create view direction cone
        this.viewCone = this.createViewCone();
        this.add(this.viewCone);

        // Create name label
        this.nameLabel = this.createNameLabel();
        this.add(this.nameLabel);
    }

    /**
     * Create the avatar sprite (circle with user initial)
     */
    private createAvatarSprite(): THREE.Sprite {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        const size = 128;
        canvas.width = size;
        canvas.height = size;

        // Draw circle background
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2 - 4, 0, Math.PI * 2);
        ctx.fillStyle = this.userColor;
        ctx.fill();

        // Draw border
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 4;
        ctx.stroke();

        // Draw initial
        ctx.fillStyle = '#FFFFFF';
        ctx.font = `bold ${size * 0.5}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const initial = this.userName.charAt(0).toUpperCase();
        ctx.fillText(initial, size / 2, size / 2);

        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;

        const material = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
        });

        const sprite = new THREE.Sprite(material);
        sprite.scale.set(COLLAB_CONFIG.AVATAR_SIZE, COLLAB_CONFIG.AVATAR_SIZE, 1);
        sprite.renderOrder = 9998;
        return sprite;
    }

    /**
     * Create view direction cone
     */
    private createViewCone(): THREE.Mesh {
        const geometry = new THREE.ConeGeometry(0.15, 0.4, 8);
        geometry.rotateX(Math.PI / 2); // Point forward

        const material = new THREE.MeshBasicMaterial({
            color: new THREE.Color(this.userColor),
            transparent: true,
            opacity: 0.5,
            depthTest: false,
            depthWrite: false,
        });

        const cone = new THREE.Mesh(geometry, material);
        cone.position.z = -0.35; // Position in front of avatar
        cone.renderOrder = 9997;
        return cone;
    }

    /**
     * Create name label sprite
     */
    private createNameLabel(): THREE.Sprite {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        canvas.width = 256;
        canvas.height = 48;

        // Draw background
        ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
        ctx.roundRect(0, 0, canvas.width, canvas.height, 6);
        ctx.fill();

        // Draw text
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 20px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.userName, canvas.width / 2, canvas.height / 2);

        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;

        const material = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
        });

        const sprite = new THREE.Sprite(material);
        sprite.scale.set(0.4, 0.075, 1);
        sprite.position.y = -COLLAB_CONFIG.AVATAR_SIZE * 0.6;
        sprite.renderOrder = 9999;
        return sprite;
    }

    /**
     * Update avatar from server data
     */
    updateFromData(data: CollabAvatarData): void {
        // Update target positions
        this.targetPosition.set(
            data.cameraPosition[0],
            data.cameraPosition[1],
            data.cameraPosition[2]
        );

        this.targetLookAt.set(
            data.cameraTarget[0],
            data.cameraTarget[1],
            data.cameraTarget[2]
        );

        // Update active state
        if (data.isActive !== undefined) {
            this.setActive(data.isActive);
        }

        // Apply positions
        this.applyPositions();
    }

    /**
     * Apply target positions to 3D elements
     */
    private applyPositions(): void {
        this.position.copy(this.targetPosition);

        // Orient to look at target
        const direction = new THREE.Vector3()
            .subVectors(this.targetLookAt, this.targetPosition)
            .normalize();

        // Create rotation to face direction
        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, -1), direction);
        this.quaternion.copy(quaternion);
    }

    /**
     * Set active/idle state
     */
    setActive(active: boolean): void {
        this.isActive = active;

        // Dim when idle
        const opacity = active ? 1 : 0.4;

        (this.avatarSprite.material as THREE.SpriteMaterial).opacity = opacity;
        (this.viewCone.material as THREE.MeshBasicMaterial).opacity = active ? 0.5 : 0.2;
        (this.nameLabel.material as THREE.SpriteMaterial).opacity = opacity;
    }

    /**
     * Update color
     */
    setColor(color: string): void {
        this.userColor = color;

        // Recreate avatar sprite
        const oldSprite = this.avatarSprite;
        this.remove(oldSprite);
        oldSprite.material.map?.dispose();
        oldSprite.material.dispose();

        this.avatarSprite = this.createAvatarSprite();
        this.add(this.avatarSprite);

        // Update cone color
        (this.viewCone.material as THREE.MeshBasicMaterial).color.set(color);
    }

    /**
     * Dispose resources
     */
    dispose(): void {
        // Avatar sprite
        this.avatarSprite.material.map?.dispose();
        this.avatarSprite.material.dispose();

        // View cone
        this.viewCone.geometry.dispose();
        (this.viewCone.material as THREE.Material).dispose();

        // Name label
        this.nameLabel.material.map?.dispose();
        this.nameLabel.material.dispose();
    }
}
